import { useState, useEffect, useRef, useCallback } from 'react';
import { FinalizedSentence } from '../types';

// Declare Web Speech API types
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface UseContinuousSpeechRecognitionOptions {
  onInterimResult?: (text: string) => void;
  onSentenceFinalized?: (sentence: FinalizedSentence) => void;
  onTopicComplete?: (sentences: FinalizedSentence[]) => void;
  onError?: (error: string) => void;
  sentencePauseMs?: number;  // Default: 1500
  topicPauseMs?: number;     // Default: 4500
  maxCommitMs?: number;      // Default: 30000 — commit pending text even without a pause
  autoStart?: boolean;       // Default: false
  language?: string;         // Default: 'en-US'
  disabled?: boolean;        // External control for muting
}

const makeSentence = (text: string, confidence: number, suffix = ''): FinalizedSentence => ({
  id: `sentence-${Date.now()}-${Math.random().toString(36).slice(2, 11)}${suffix}`,
  text: text.trim(),
  confidence,
  timestamp: new Date().toISOString(),
  isNew: true
});

export function useContinuousSpeechRecognition(
  options: UseContinuousSpeechRecognitionOptions = {}
) {
  const {
    onInterimResult,
    onSentenceFinalized,
    onTopicComplete,
    onError,
    sentencePauseMs = 1500,
    topicPauseMs = 4500,
    maxCommitMs = 30000,
    autoStart = false,
    language = 'en-US',
    disabled = false
  } = options;

  // State
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalizedSentences, setFinalizedSentences] = useState<FinalizedSentence[]>([]);
  const [pauseDuration, setPauseDuration] = useState(0);

  // Refs
  const recognitionRef = useRef<any>(null);
  const sentenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const topicTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const pauseDurationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldKeepListeningRef = useRef(false);
  const isRestartingRef = useRef(false);
  const lastResultTimeRef = useRef<number>(Date.now());
  const accumulatedInterimRef = useRef<string>('');
  // Mirror of finalizedSentences state — updated synchronously so timers can read it
  // without relying on React state-updater side effects (which run lazily in React 18).
  const finalizedSentencesRef = useRef<FinalizedSentence[]>([]);
  const interimFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxCommitTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Final result waiting out sentencePauseMs before it joins the queue
  const pendingFinalRef = useRef<{ text: string; confidence: number } | null>(null);
  const lastInterimIndexRef = useRef<number>(-1);
  // Words already committed from an in-progress result, stripped from its later updates
  const committedInterimRef = useRef<{ resultIndex: number; wordCount: number } | null>(null);

  // Update callback refs when they change
  const onInterimResultRef = useRef(onInterimResult);
  const onSentenceFinalizedRef = useRef(onSentenceFinalized);
  const onTopicCompleteRef = useRef(onTopicComplete);
  const onErrorRef = useRef(onError);
  const finalizeSentenceRef = useRef<((text: string, confidence: number) => void) | null>(null);
  const flushPendingFinalRef = useRef<(() => void) | null>(null);
  const ensureMaxCommitTimerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onInterimResultRef.current = onInterimResult;
    onSentenceFinalizedRef.current = onSentenceFinalized;
    onTopicCompleteRef.current = onTopicComplete;
    onErrorRef.current = onError;
  }, [onInterimResult, onSentenceFinalized, onTopicComplete, onError]);

  // Clear all timers helper
  const clearAllTimers = useCallback(() => {
    if (sentenceTimerRef.current) {
      clearTimeout(sentenceTimerRef.current);
      sentenceTimerRef.current = null;
    }
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (pauseDurationIntervalRef.current) {
      clearInterval(pauseDurationIntervalRef.current);
      pauseDurationIntervalRef.current = null;
    }
    if (interimFallbackTimerRef.current) {
      clearTimeout(interimFallbackTimerRef.current);
      interimFallbackTimerRef.current = null;
    }
    if (maxCommitTimerRef.current) {
      clearTimeout(maxCommitTimerRef.current);
      maxCommitTimerRef.current = null;
    }
    pendingFinalRef.current = null;
    pauseStartTimeRef.current = null;
    setPauseDuration(0);
  }, []);

  // Start pause duration tracking
  const startPauseDurationTracking = useCallback(() => {
    pauseStartTimeRef.current = Date.now();

    // Update pause duration every 100ms for UI
    pauseDurationIntervalRef.current = setInterval(() => {
      if (pauseStartTimeRef.current) {
        const duration = Date.now() - pauseStartTimeRef.current;
        setPauseDuration(duration);
      }
    }, 100);
  }, []);

  // Stop pause duration tracking
  const stopPauseDurationTracking = useCallback(() => {
    if (pauseDurationIntervalRef.current) {
      clearInterval(pauseDurationIntervalRef.current);
      pauseDurationIntervalRef.current = null;
    }
    pauseStartTimeRef.current = null;
    setPauseDuration(0);
  }, []);

  const clearMaxCommitTimer = useCallback(() => {
    if (maxCommitTimerRef.current) {
      clearTimeout(maxCommitTimerRef.current);
      maxCommitTimerRef.current = null;
    }
  }, []);

  // Finalize sentence and add to queue
  const finalizeSentence = useCallback((text: string, confidence: number) => {
    if (!text.trim()) return;

    const sentence = makeSentence(text, confidence);

    // Keep the ref in sync — this is the authoritative source for timers/flush paths.
    const newQueue = [...finalizedSentencesRef.current, sentence];
    finalizedSentencesRef.current = newQueue;

    // Update state for UI rendering.
    setFinalizedSentences(newQueue);

    // Auto-flush if queue gets too large (10 sentences) — done outside a state updater
    // to avoid the React strict-mode double-invocation problem.
    if (newQueue.length >= 10) {
      const captured = [...newQueue];
      clearMaxCommitTimer();
      finalizedSentencesRef.current = [];
      setFinalizedSentences([]);
      setTimeout(() => {
        if (onTopicCompleteRef.current) {
          onTopicCompleteRef.current(captured);
        }
      }, 100);
    }

    // Mark as not new after 3 seconds for fade animation
    const sentenceId = sentence.id;
    const markNotNew = (prev: FinalizedSentence[]) =>
      prev.map(s => s.id === sentenceId ? { ...s, isNew: false } : s);
    setTimeout(() => {
      setFinalizedSentences(markNotNew);
    }, 3000);

    if (onSentenceFinalizedRef.current) {
      onSentenceFinalizedRef.current(sentence);
    }

    if (finalizedSentencesRef.current.length > 0) {
      ensureMaxCommitTimerRef.current?.();
    }

    // Start topic pause timer (but clear any existing one first)
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    stopPauseDurationTracking();

    startPauseDurationTracking();
    topicTimerRef.current = setTimeout(() => {
      // Read synchronously from the ref — avoids the React 18 state-updater side-effect
      // race where the updater runs lazily and captured is empty when setTimeout(0) fires.
      const captured = [...finalizedSentencesRef.current];
      finalizedSentencesRef.current = [];
      setFinalizedSentences([]);
      clearMaxCommitTimer();

      if (captured.length > 0 && onTopicCompleteRef.current) {
        onTopicCompleteRef.current(captured);
      }
      stopPauseDurationTracking();
    }, topicPauseMs);
  }, [topicPauseMs, startPauseDurationTracking, stopPauseDurationTracking, clearMaxCommitTimer]);

  // Update finalizeSentence ref whenever it changes
  useEffect(() => {
    finalizeSentenceRef.current = finalizeSentence;
  }, [finalizeSentence]);

  // Clear sentence timer
  const clearSentenceTimer = useCallback(() => {
    if (sentenceTimerRef.current) {
      clearTimeout(sentenceTimerRef.current);
      sentenceTimerRef.current = null;
    }
  }, []);

  // Queue a final result now instead of waiting out sentencePauseMs
  const flushPendingFinal = useCallback(() => {
    const pending = pendingFinalRef.current;
    clearSentenceTimer();
    pendingFinalRef.current = null;
    if (pending) finalizeSentenceRef.current?.(pending.text, pending.confidence);
  }, [clearSentenceTimer]);

  // Clear topic timer
  const clearTopicTimer = useCallback(() => {
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    stopPauseDurationTracking();
  }, [stopPauseDurationTracking]);

  // Commit everything pending — queue, waiting final, in-progress speech — without a pause
  const flushOnMaxAge = useCallback(() => {
    maxCommitTimerRef.current = null;
    const toSend: FinalizedSentence[] = [...finalizedSentencesRef.current];
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);

    const pending = pendingFinalRef.current;
    clearSentenceTimer();
    pendingFinalRef.current = null;
    if (pending) toSend.push(makeSentence(pending.text, pending.confidence));

    const interim = accumulatedInterimRef.current.trim();
    if (interim.length >= 3) {
      toSend.push(makeSentence(interim, 0.8, '-max'));
      const prior = committedInterimRef.current;
      const alreadyCommitted = prior && prior.resultIndex === lastInterimIndexRef.current ? prior.wordCount : 0;
      committedInterimRef.current = {
        resultIndex: lastInterimIndexRef.current,
        wordCount: alreadyCommitted + interim.split(/\s+/).length
      };
      accumulatedInterimRef.current = '';
      setInterimTranscript('');
      if (interimFallbackTimerRef.current) {
        clearTimeout(interimFallbackTimerRef.current);
        interimFallbackTimerRef.current = null;
      }
    }

    clearTopicTimer();
    if (toSend.length > 0 && onTopicCompleteRef.current) {
      onTopicCompleteRef.current(toSend);
    }
  }, [clearSentenceTimer, clearTopicTimer]);

  const ensureMaxCommitTimer = useCallback(() => {
    if (maxCommitTimerRef.current) return;
    maxCommitTimerRef.current = setTimeout(flushOnMaxAge, maxCommitMs);
  }, [flushOnMaxAge, maxCommitMs]);

  useEffect(() => {
    flushPendingFinalRef.current = flushPendingFinal;
    ensureMaxCommitTimerRef.current = ensureMaxCommitTimer;
  }, [flushPendingFinal, ensureMaxCommitTimer]);

  // Initialize speech recognition ONCE on mount
  useEffect(() => {
    const initRecognition = async () => {
      try {
        // Check browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
          console.warn('[Continuous STT] Speech recognition not supported in this browser');
          setIsSupported(false);
          return;
        }

        setIsSupported(true);

        // Request microphone permission
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          setPermissionGranted(true);
        } catch (permError) {
          console.warn('[Continuous STT] Microphone permission not granted:', permError);
          setPermissionGranted(false);
        }

        // Create recognition instance
        const recognition = new SpeechRecognition();
        recognition.continuous = true;        // Continuous listening
        recognition.interimResults = true;    // Show live transcription
        recognition.lang = language;
        recognition.maxAlternatives = 1;

        const handleInterimFallback = () => {
          const accumulated = accumulatedInterimRef.current;
          if (accumulated && accumulated.trim().length >= 3) {
            setInterimTranscript('');
            accumulatedInterimRef.current = '';
            if (finalizeSentenceRef.current) {
              finalizeSentenceRef.current(accumulated, 0.8);
            }
          }
        };

        const makeSentenceTimerHandler = (transcript: string, confidence: number) => () => {
          sentenceTimerRef.current = null;
          pendingFinalRef.current = null;
          if (finalizeSentenceRef.current) {
            finalizeSentenceRef.current(transcript, confidence);
          }
        };

        recognition.onstart = () => {
          setIsListening(true);
          isRestartingRef.current = false;
          lastResultTimeRef.current = Date.now();
          committedInterimRef.current = null;
        };

        const handleInterimResult = (transcript: string) => {
          setInterimTranscript(transcript);
          accumulatedInterimRef.current = transcript;
          if (onInterimResultRef.current) onInterimResultRef.current(transcript);
          flushPendingFinalRef.current?.();
          clearTopicTimer();
          if (transcript.trim()) ensureMaxCommitTimerRef.current?.();
          if (interimFallbackTimerRef.current) clearTimeout(interimFallbackTimerRef.current);
          interimFallbackTimerRef.current = setTimeout(handleInterimFallback, sentencePauseMs + 1000);
        };

        const handleFinalResult = (transcript: string, confidence: number) => {
          if (interimFallbackTimerRef.current) {
            clearTimeout(interimFallbackTimerRef.current);
            interimFallbackTimerRef.current = null;
          }
          setInterimTranscript('');
          accumulatedInterimRef.current = '';
          const tooShort = transcript.trim().length < 3;
          const lowConfidence = confidence < 0.3;
          if (tooShort || lowConfidence) return;
          flushPendingFinalRef.current?.();
          pendingFinalRef.current = { text: transcript, confidence };
          sentenceTimerRef.current = setTimeout(makeSentenceTimerHandler(transcript, confidence), sentencePauseMs);
          ensureMaxCommitTimerRef.current?.();
        };

        recognition.onresult = (event: any) => {
          const result = event.results[event.resultIndex];
          let transcript: string = result[0].transcript;
          const confidence = result[0].confidence;
          lastResultTimeRef.current = Date.now();
          const committed = committedInterimRef.current;
          if (committed && committed.resultIndex === event.resultIndex) {
            transcript = transcript.trim().split(/\s+/).slice(committed.wordCount).join(' ');
            if (result.isFinal) committedInterimRef.current = null;
          } else if (committed) {
            committedInterimRef.current = null;
          }
          if (!result.isFinal) lastInterimIndexRef.current = event.resultIndex;
          if (!result.isFinal) {
            handleInterimResult(transcript);
          } else {
            handleFinalResult(transcript, confidence);
          }
        };

        const handleNoSpeechError = () => {
          const shouldRestart = shouldKeepListeningRef.current && !disabled;
          if (!shouldRestart) return;
          isRestartingRef.current = true;
          try {
            recognitionRef.current?.stop();
          } catch (e) {
            // ignore — onend will still drive the restart path
          }
        };

        const getErrorMessage = (errorCode: string): string | null => {
          switch (errorCode) {
            case 'audio-capture': return 'No microphone found. Please check your device.';
            case 'network': return 'Network error. Please check your connection.';
            default: return `Speech recognition error: ${errorCode}`;
          }
        };

        recognition.onerror = (event: any) => {
          console.error('[Continuous STT] Speech recognition error:', event.error);

          if (event.error === 'no-speech') {
            handleNoSpeechError();
            return;
          }

          if (event.error === 'not-allowed') {
            setPermissionGranted(false);
            shouldKeepListeningRef.current = false;
            setIsListening(false);
            if (onErrorRef.current) {
              onErrorRef.current('Microphone permission denied. Please enable it in your browser settings.');
            }
            return;
          }

          const errorMsg = getErrorMessage(event.error);
          if (errorMsg && onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
        };

        function attemptRestart(retry: boolean) {
          try {
            recognitionRef.current?.start();
          } catch (e) {
            console.error('[Continuous STT] Auto-restart failed:', e);
            if (retry) {
              setTimeout(retryRestart, 500);
            } else {
              setIsListening(false);
              isRestartingRef.current = false;
            }
          }
        }
        function retryRestart() { attemptRestart(false); }
        function startRestart() { attemptRestart(true); }

        recognition.onend = () => {
          // Only flip UI to "off" if we genuinely intend to stop. During an
          // auto-restart cycle (e.g. Chrome no-speech timeout), keep the mic
          // indicator solid so users don't see it flicker every ~10s.
          if (!shouldKeepListeningRef.current || disabled) {
            setIsListening(false);
            isRestartingRef.current = false;
            return;
          }

          isRestartingRef.current = true;
          setTimeout(startRestart, 300);
        };

        recognitionRef.current = recognition;

      } catch (error) {
        console.error('[Continuous STT] Failed to initialize speech recognition:', error);
        setIsSupported(false);
      }
    };

    initRecognition();

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.warn('[Continuous STT] Error during cleanup abort:', e);
        }
      }
      if (sentenceTimerRef.current) clearTimeout(sentenceTimerRef.current);
      if (topicTimerRef.current) clearTimeout(topicTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (pauseDurationIntervalRef.current) clearInterval(pauseDurationIntervalRef.current);
      if (interimFallbackTimerRef.current) clearTimeout(interimFallbackTimerRef.current);
      if (maxCommitTimerRef.current) clearTimeout(maxCommitTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle disabled prop changes
  useEffect(() => {
    if (disabled && isListening) {
      // Call stop directly - it's defined below and uses refs internally
      shouldKeepListeningRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.warn('[Continuous STT] Error stopping recognition:', e);
        }
      }
      clearAllTimers();
      setInterimTranscript('');
      accumulatedInterimRef.current = '';
    }
  }, [disabled, isListening, clearAllTimers]);

  // Heartbeat to prevent Chrome timeout (restart every 45s)
  useEffect(() => {
    if (!isListening) return;

    heartbeatTimerRef.current = setInterval(() => {
      if (recognitionRef.current && isListening) {
        try {
          recognitionRef.current.stop(); // Will trigger onend → auto-restart
        } catch (e) {
          console.error('[Continuous STT] Heartbeat restart failed:', e);
        }
      }
    }, 45000); // 45 seconds

    return () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [isListening]);

  // Start continuous listening
  const start = useCallback(async () => {
    if (!recognitionRef.current) {
      if (onErrorRef.current) {
        onErrorRef.current('Speech recognition not available in this browser. Please use Chrome, Edge, or Safari 14.1+.');
      }
      return;
    }

    if (disabled) {
      return;
    }

    // Check if already running via ref instead of state
    if (shouldKeepListeningRef.current) {
      return;
    }

    // Re-check current mic permission live rather than trusting the stale
    // mount-time result — the user may have granted/revoked access since.
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionGranted(true);
    } catch (permError) {
      console.warn('[Continuous STT] Microphone permission not granted:', permError);
      setPermissionGranted(false);
      if (onErrorRef.current) {
        onErrorRef.current('Microphone permission denied. Please enable it in your browser settings.');
      }
      return;
    }

    shouldKeepListeningRef.current = true;

    try {
      recognitionRef.current.start();
    } catch (error: any) {
      console.error('[Continuous STT] Failed to start recognition:', error);

      // If recognition is already running, stop and restart
      if (error.message && error.message.includes('already started')) {
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
          } catch (e) {
            console.error('[Continuous STT] Retry failed:', e);
            if (onErrorRef.current) {
              onErrorRef.current('Failed to start microphone');
            }
          }
        }, 100);
      } else {
        if (onErrorRef.current) {
          onErrorRef.current('Failed to start microphone: ' + error.message);
        }
      }
    }
  }, [disabled]);

  // Stop continuous listening
  const stop = useCallback(() => {
    shouldKeepListeningRef.current = false;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.warn('[Continuous STT] Error stopping recognition:', e);
      }
    }

    const pending = pendingFinalRef.current;

    // Clear all timers first
    clearAllTimers();

    // Flush queued sentences + any accumulated interim text — read from the ref
    // synchronously so we don't rely on a React state-updater side effect.
    const accumulated = accumulatedInterimRef.current;
    const toSend: FinalizedSentence[] = [...finalizedSentencesRef.current];
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);
    if (pending) toSend.push(makeSentence(pending.text, pending.confidence));

    // Also include any accumulated interim text as a sentence
    if (accumulated && accumulated.trim().length >= 3) {
      toSend.push({
        id: `sentence-${Date.now()}-stop`,
        text: accumulated.trim(),
        confidence: 0.8,
        timestamp: new Date().toISOString(),
        isNew: true
      });
    }

    if (toSend.length > 0 && onTopicCompleteRef.current) {
      onTopicCompleteRef.current(toSend);
    }

    // Clear interim transcript
    setInterimTranscript('');
    accumulatedInterimRef.current = '';
  }, [clearAllTimers]);

  // Clear queue
  const clearQueue = useCallback(() => {
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);
    clearTopicTimer();
    clearMaxCommitTimer();
  }, [clearTopicTimer, clearMaxCommitTimer]);

  // Send queue immediately (manual trigger)
  const sendQueueNow = useCallback(() => {
    const captured = [...finalizedSentencesRef.current];
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);
    clearTopicTimer();
    clearMaxCommitTimer();
    if (captured.length > 0 && onTopicCompleteRef.current) {
      onTopicCompleteRef.current(captured);
    }
  }, [clearTopicTimer, clearMaxCommitTimer]);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart && isSupported && permissionGranted && !isListening) {
      start();
    }
  }, [autoStart, isSupported, permissionGranted, isListening, start]);

  return {
    // State
    isListening,
    isSupported,
    permissionGranted,
    interimTranscript,
    finalizedSentences,
    pauseDuration,

    // Methods
    start,
    stop,
    clearQueue,
    sendQueueNow
  };
}
